import { SelectableText } from "@/components/flashcard";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  LoaderCircle,
  RotateCcw,
  Settings,
  Volume2,
} from "lucide-react";
import Image from "next/image";
import React, { useEffect, useRef } from "react";
import { ExerciseData, ExerciseOption } from "../types";
import { usesNonLatinScript } from "../utils";
import ExplanationPanel from "./ExplanationPanel";

const OPTION_NUMBERS = ["1", "2", "3", "4", "5", "6", "7", "8"];

type WordItem = string | ExerciseOption;

interface CanvasPracticePanelProps {
  className?: string;
  exerciseData: ExerciseData;
  selectedLanguage: string;
  selectedLanguageCode: string;
  canGoToPrevious: boolean;
  historyIndex: number;
  historyLength: number;
  showSettings: boolean;
  setShowSettings: (value: boolean) => void;
  showAudioSettings: boolean;
  setShowAudioSettings: (value: boolean) => void;
  soundEffectsEnabled: boolean;
  setSoundEffectsEnabled: (value: boolean) => void;
  ttsEnabled: boolean;
  setTtsEnabled: (value: boolean) => void;
  hideTranslations: boolean;
  setHideTranslations: (value: boolean) => void;
  hideTranscription: boolean;
  setHideTranscription: (value: boolean) => void;
  showOptions: boolean;
  setShowOptions: (value: boolean) => void;
  autoSubmit: boolean;
  setAutoSubmit: (value: boolean) => void;
  typedAnswer: string;
  currentSentence: WordItem[];
  poolWords: WordItem[];
  discardedWordKeys: Set<string>;
  getWordKey: (word: WordItem) => string;
  showNextButton: boolean;
  showSuccessFeedback: boolean;
  levelIncrease: number;
  streak?: number;
  isLoading: boolean;
  feedback: string;
  isCorrect: boolean | null;
  showExplanation: boolean;
  setShowExplanation: (value: boolean) => void;
  initialLanguageLevel: string;
  onPrevious: () => void;
  onNextNavigation: () => void;
  onNextExercise: () => void;
  onReviewSuccess: () => void;
  onRefresh: () => void;
  onCheckAnswer: (answer?: string, meta?: { wordKey: string }) => void;
  onTypedAnswerChange: (value: string) => void;
  onWordClick: (word: WordItem, fromSentence: boolean, index: number) => void;
  onFlashcardWordClick: (word: string, position: { x: number; y: number }) => void;
  onSpeakSentence: () => void;
  isSpeaking: boolean;
  ttsSpeed?: number;
  onTtsSpeedChange?: (speed: number) => void;
}

const ToggleRow = ({
  checked,
  label,
  description,
  onToggle,
}: {
  checked: boolean;
  label: string;
  description?: string;
  onToggle: () => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onToggle}
    className="flex min-h-12 w-full items-center justify-between gap-4 rounded-lg px-3 py-2 text-left transition-colors hover:bg-white/[0.06]"
  >
    <span>
      <span className="block text-sm font-medium text-white/85">{label}</span>
      {description && <span className="block text-xs text-white/45">{description}</span>}
    </span>
    <span
      className={`relative inline-block h-6 w-11 shrink-0 rounded-full border transition-colors ${
        checked ? "border-[#FD954F] bg-[#FD954F]" : "border-white/15 bg-white/10"
      }`}
      aria-hidden="true"
    >
      <span
        className={`absolute top-0.5 h-[18px] w-[18px] rounded-full bg-[#0F0F14] transition-transform motion-reduce:transition-none ${
          checked ? "translate-x-[19px]" : "translate-x-0.5"
        }`}
      />
    </span>
  </button>
);

const getWordText = (word: WordItem) => (typeof word === "string" ? word : word.text);

const CanvasPracticePanel: React.FC<CanvasPracticePanelProps> = ({
  className,
  exerciseData,
  selectedLanguage,
  selectedLanguageCode,
  canGoToPrevious,
  historyIndex,
  historyLength,
  showSettings,
  setShowSettings,
  showAudioSettings,
  setShowAudioSettings,
  soundEffectsEnabled,
  setSoundEffectsEnabled,
  ttsEnabled,
  setTtsEnabled,
  hideTranslations,
  setHideTranslations,
  hideTranscription,
  setHideTranscription,
  showOptions,
  setShowOptions,
  autoSubmit,
  setAutoSubmit,
  typedAnswer,
  currentSentence,
  poolWords,
  discardedWordKeys,
  getWordKey,
  showNextButton,
  showSuccessFeedback,
  levelIncrease,
  streak = 0,
  isLoading,
  feedback,
  isCorrect,
  showExplanation,
  setShowExplanation,
  initialLanguageLevel,
  onPrevious,
  onNextNavigation,
  onNextExercise,
  onReviewSuccess,
  onRefresh,
  onCheckAnswer,
  onTypedAnswerChange,
  onWordClick,
  onFlashcardWordClick,
  onSpeakSentence,
  isSpeaking,
  ttsSpeed = 1.0,
  onTtsSpeedChange,
}) => {
  const settingsOpen = showSettings || showAudioSettings;
  const settingsRef = useRef<HTMLDivElement>(null);
  const displayAnswer =
    typedAnswer ||
    (currentSentence.length > 0 ? currentSentence.map(getWordText).join(" ") : "___");
  const isAlmostCorrect = feedback.includes("Almost");

  const toggleSettings = () => {
    const nextOpen = !settingsOpen;
    setShowSettings(nextOpen);
    setShowAudioSettings(false);
  };

  useEffect(() => {
    if (!settingsOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettings(false);
        setShowAudioSettings(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowSettings(false);
        setShowAudioSettings(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [settingsOpen, setShowAudioSettings, setShowSettings]);

  useEffect(() => {
    if (!showOptions || settingsOpen || isLoading || showNextButton) return;

    const handleNumberKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.altKey || event.metaKey) return;

      const activeElement = document.activeElement;
      const isTypingInField =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          (activeElement as HTMLElement).isContentEditable);

      if (isTypingInField) return;

      const num = parseInt(event.key, 10);
      if (isNaN(num) || num < 1) return;

      const allChoices = [
        ...currentSentence.map((word, idx) => ({ word, fromSentence: true, originalIndex: idx })),
        ...poolWords.map((word, idx) => ({ word, fromSentence: false, originalIndex: idx })),
      ];

      const optionIndex = num - 1;
      if (optionIndex >= allChoices.length) return;

      const target = allChoices[optionIndex];
      const wordKey = getWordKey(target.word);
      if (discardedWordKeys.has(wordKey)) return;

      event.preventDefault();
      if (autoSubmit) {
        const sentenceText = getWordText(target.word);
        onTypedAnswerChange(sentenceText);
        onCheckAnswer(sentenceText, { wordKey });
      } else {
        onWordClick(target.word, target.fromSentence, target.originalIndex);
      }
    };

    window.addEventListener("keydown", handleNumberKeyDown);
    return () => window.removeEventListener("keydown", handleNumberKeyDown);
  }, [
    showOptions,
    settingsOpen,
    isLoading,
    showNextButton,
    currentSentence,
    poolWords,
    discardedWordKeys,
    getWordKey,
    autoSubmit,
    onTypedAnswerChange,
    onCheckAnswer,
    onWordClick,
  ]);

  return (
    <section
      className={`mt-5 rounded-lg border border-white/10 bg-[#15161B] p-4 sm:p-6 md:p-7 ${className || ""}`}
      aria-label="Fill in the gap exercise"
      aria-busy={isLoading}
    >
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
        <div className="flex items-center gap-1" aria-label="Exercise history">
          <button
            type="button"
            onClick={onPrevious}
            disabled={!canGoToPrevious || isLoading}
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 text-white/65 transition-colors hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Previous exercise"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <span className="min-w-16 px-2 text-center text-xs tabular-nums text-white/45">
            {historyIndex + 1} of {Math.max(historyLength, 1)}
          </span>
          <button
            type="button"
            onClick={onNextNavigation}
            disabled={isLoading}
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 text-white/65 transition-colors hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Next exercise in history"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div ref={settingsRef} className="relative flex items-center gap-2">
          <button
            type="button"
            onClick={toggleSettings}
            disabled={isLoading}
            className={`inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors disabled:opacity-40 ${
              settingsOpen
                ? "border-[#FD954F]/60 bg-[#FD954F]/10 text-[#FD954F]"
                : "border-white/10 text-white/65 hover:bg-white/[0.06] hover:text-white"
            }`}
            aria-expanded={settingsOpen}
            aria-controls="visual-practice-settings"
          >
            <Settings className="h-4 w-4" aria-hidden="true" />
            Settings
          </button>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 text-white/65 transition-colors hover:bg-white/[0.06] hover:text-white disabled:opacity-40"
            aria-label="Load a different exercise"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
          </button>

          {settingsOpen && (
            <div
              id="visual-practice-settings"
              className="absolute right-0 top-full z-40 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-lg border border-white/10 bg-[#1B1C22] p-2 shadow-xl shadow-black/30"
            >
              <ToggleRow
                checked={!hideTranslations}
                label="Show translation"
                onToggle={() => setHideTranslations(!hideTranslations)}
              />
              {usesNonLatinScript(selectedLanguageCode, selectedLanguage, exerciseData) && (
                <ToggleRow
                  checked={!hideTranscription}
                  label="Show transcription"
                  description="Available for supported writing systems"
                  onToggle={() => setHideTranscription(!hideTranscription)}
                />
              )}
              <div className="my-1 border-t border-white/10" />
              <ToggleRow
                checked={soundEffectsEnabled}
                label="Sound effects"
                onToggle={() => setSoundEffectsEnabled(!soundEffectsEnabled)}
              />
              <ToggleRow
                checked={ttsEnabled}
                label="Text to speech"
                onToggle={() => setTtsEnabled(!ttsEnabled)}
              />
              {ttsEnabled && onTtsSpeedChange && (
                <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.04] p-2.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-white/80">Speaking speed</span>
                    <span className="tabular-nums font-semibold text-[#FD954F]">
                      {ttsSpeed.toFixed(1)}×
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0.5}
                    max={1.5}
                    step={0.05}
                    value={ttsSpeed}
                    onChange={(e) => onTtsSpeedChange(parseFloat(e.target.value))}
                    className="mt-2 h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-white/20 accent-[#FD954F]"
                    aria-label="Tutor speaking speed"
                    aria-valuetext={`${ttsSpeed.toFixed(1)} times normal speed`}
                  />
                  <div className="mt-2 flex justify-between gap-1">
                    {[0.75, 1.0, 1.25].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => onTtsSpeedChange(preset)}
                        className={`flex-1 rounded py-1 text-[11px] font-medium transition-colors ${
                          Math.abs(ttsSpeed - preset) < 0.03
                            ? "border border-[#FD954F]/40 bg-[#FD954F]/20 text-[#FD954F]"
                            : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
                        }`}
                      >
                        {preset === 1.0 ? "1.0× (Normal)" : `${preset}×`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="border-b border-white/10 pb-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/15 bg-black/40">
              <Image
                src="/ui/brand-kit/marks/chickytutor-face-mark-128.png"
                alt=""
                width={32}
                height={32}
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#FD954F]">
                Complete the sentence
              </p>
              {(isCorrect === true || isAlmostCorrect || Boolean(feedback)) && (
                <p className="text-xs text-white/55">
                  {isCorrect === true
                    ? "Great job!"
                    : isAlmostCorrect
                      ? "Almost there, take another look"
                      : feedback}
                </p>
              )}
            </div>
          </div>
          {ttsEnabled && (
            <div className="flex items-center gap-1.5">
              {ttsSpeed !== 1.0 && (
                <span
                  className="rounded border border-[#FD954F]/30 bg-[#FD954F]/10 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-[#FD954F]"
                  title={`Speed: ${ttsSpeed.toFixed(1)}×`}
                >
                  {ttsSpeed.toFixed(1)}×
                </span>
              )}
              <button
                type="button"
                onClick={onSpeakSentence}
                disabled={isSpeaking || isLoading}
                className={`flex h-11 w-11 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed ${
                  isSpeaking
                    ? "border-[#FD954F]/60 bg-[#FD954F]/10 text-[#FD954F]"
                    : "border-white/10 text-white/55 hover:bg-white/[0.06] hover:text-white"
                }`}
                aria-label={isSpeaking ? "Pronouncing sentence" : "Pronounce sentence"}
              >
                <Volume2
                  className={`h-4 w-4 ${isSpeaking ? "motion-safe:animate-pulse" : ""}`}
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
        </div>

        <div className="text-xl font-semibold leading-relaxed text-white sm:text-2xl">
          <SelectableText
            onWordClick={onFlashcardWordClick}
            targetLanguage={selectedLanguage}
            languageCode={selectedLanguageCode}
            className="inline"
          >
            {exerciseData.content.split(/(_+)/).map((part, index) =>
              part.startsWith("_") ? (
                <span
                  key={index}
                  className="mx-1 inline-block min-w-20 border-b-2 border-[#FD954F] px-1 text-center text-[#FD954F]"
                >
                  {displayAnswer}
                </span>
              ) : (
                <span key={index}>{part}</span>
              )
            )}
          </SelectableText>
        </div>

        {!hideTranscription && exerciseData.transcription && (
          <p className="mt-4 text-sm leading-relaxed text-white/50">{exerciseData.transcription}</p>
        )}
        {!hideTranslations && (
          <p className="mt-2 text-base leading-relaxed text-white/65">
            &quot;{exerciseData.translation}&quot;
          </p>
        )}
      </div>

      <div className="mt-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <label htmlFor="visual-practice-answer" className="text-sm font-medium text-white/80">
            Your answer
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              role="switch"
              aria-checked={autoSubmit}
              onClick={() => setAutoSubmit(!autoSubmit)}
              disabled={isLoading}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white disabled:opacity-40"
            >
              <span>Auto-submit</span>
              <span
                className={`relative inline-block h-5 w-9 shrink-0 rounded-full border transition-colors ${
                  autoSubmit ? "border-[#FD954F] bg-[#FD954F]" : "border-white/15 bg-white/10"
                }`}
                aria-hidden="true"
              >
                <span
                  className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-[#0F0F14] transition-transform motion-reduce:transition-none ${
                    autoSubmit ? "translate-x-[17px]" : "translate-x-0.5"
                  }`}
                />
              </span>
            </button>
            <button
              type="button"
              onClick={() => setShowOptions(!showOptions)}
              disabled={isLoading}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white disabled:opacity-40"
              aria-expanded={showOptions}
            >
              {showOptions ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
              {showOptions ? "Hide choices" : "Show choices"}
            </button>
          </div>
        </div>
        <input
          id="visual-practice-answer"
          type="text"
          value={typedAnswer}
          onChange={(event) => onTypedAnswerChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && typedAnswer.trim() && !isLoading && !showNextButton) {
              onCheckAnswer();
            }
          }}
          disabled={isLoading || showNextButton}
          placeholder="Type your answer"
          className="min-h-12 w-full rounded-lg border border-white/15 bg-[#1B1C22] px-4 text-base text-white outline-none transition-colors placeholder:text-white/30 focus:border-[#FD954F] focus:ring-2 focus:ring-[#FD954F]/20 disabled:cursor-not-allowed disabled:opacity-55"
          autoComplete="off"
        />
      </div>

      {showOptions && (
        <div className="mt-5 rounded-xl border border-white/[0.08] bg-[#121318] p-3 sm:p-4">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
            {currentSentence.map((word, index) => (
              <button
                type="button"
                key={`sentence-${index}`}
                onClick={() => onWordClick(word, true, index)}
                disabled={isLoading || showNextButton}
                className="group relative flex min-h-[64px] flex-col items-center justify-center rounded-xl border border-[#FD954F] bg-[#FD954F] px-3 py-2.5 text-center shadow-[0_0_20px_rgba(253,149,79,0.3)] transition-all duration-200 hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-55"
                aria-pressed="true"
              >
                <span className="absolute left-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-md bg-[#17120E]/15 text-[11px] font-bold text-[#17120E]">
                  ✓
                </span>
                <span className="block text-base font-bold text-[#17120E] sm:text-lg">
                  {getWordText(word)}
                </span>
                {typeof word !== "string" && !hideTranscription && word.transcription && (
                  <span className="block text-xs font-medium text-[#17120E]/75 mt-0.5">
                    {word.transcription}
                  </span>
                )}
              </button>
            ))}

            {poolWords.map((word, index) => {
              const wordKey = getWordKey(word);
              const isDiscarded = discardedWordKeys.has(wordKey);
              const wordText = getWordText(word);
              const optionNumber =
                OPTION_NUMBERS[currentSentence.length + index] ||
                String(currentSentence.length + index + 1);

              return (
                <button
                  key={`pool-${index}`}
                  type="button"
                  onClick={() => {
                    if (!isDiscarded) onWordClick(word, false, index);
                  }}
                  disabled={isDiscarded || isLoading || showNextButton}
                  aria-keyshortcuts={optionNumber}
                  title={
                    isDiscarded ? `${wordText} (already tried)` : `Press ${optionNumber} to submit`
                  }
                  className={`group relative flex min-h-[64px] flex-col items-center justify-center rounded-xl border px-3 py-2.5 text-center transition-all duration-200 ${
                    isDiscarded
                      ? "cursor-not-allowed border-transparent bg-white/[0.02] text-white/30 opacity-40"
                      : "border-white/[0.08] bg-[#1A1B22] text-white/90 shadow-sm hover:border-white/20 hover:bg-[#23242E] hover:text-white motion-safe:hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]"
                  } disabled:cursor-not-allowed`}
                  aria-label={isDiscarded ? `${wordText}, not the answer` : wordText}
                >
                  <span
                    className={`absolute left-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-md text-[10px] font-mono font-medium transition-colors ${
                      isDiscarded
                        ? "border-transparent text-white/25"
                        : "border border-white/[0.08] bg-white/[0.04] text-white/45 group-hover:border-white/15 group-hover:text-white/70"
                    }`}
                  >
                    {isDiscarded ? "✕" : optionNumber}
                  </span>
                  <span
                    className={`block text-base font-medium sm:text-lg ${
                      isDiscarded
                        ? "line-through text-white/30"
                        : "text-white/90 group-hover:text-white"
                    }`}
                  >
                    {wordText}
                  </span>
                  {typeof word !== "string" && !hideTranscription && word.transcription && (
                    <span
                      className={`block text-xs font-normal mt-0.5 ${
                        isDiscarded ? "text-white/20" : "text-white/40 group-hover:text-white/60"
                      }`}
                    >
                      {word.transcription}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isCorrect === true && showNextButton ? (
        <div
          className={`mt-6 rounded-lg border border-emerald-400/35 bg-emerald-400/[0.08] p-4 ${
            showSuccessFeedback ? "motion-safe:animate-in motion-safe:fade-in" : ""
          }`}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <div className="relative mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full border border-emerald-400/40 bg-black/30">
              <Image
                src="/ui/brand-kit/marks/chickytutor-face-mark-128.png"
                alt=""
                width={28}
                height={28}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-white">Correct</p>
                <CheckCircle2 className="h-4 w-4 text-emerald-400" aria-hidden="true" />
              </div>
              {feedback && <p className="mt-1 text-sm text-white/70">{feedback}</p>}
              {levelIncrease > 0 && (
                <p className="mt-1 text-sm text-emerald-300">
                  You moved up {levelIncrease} {levelIncrease === 1 ? "level" : "levels"}.
                </p>
              )}
              {streak > 0 && streak % 5 === 0 && (
                <div className="mt-2 rounded-md border border-[#FD954F]/30 bg-[#FD954F]/10 px-3 py-1.5 text-xs text-[#FD954F]">
                  🎯 <strong>{streak} in a row!</strong> Great commute momentum.
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onReviewSuccess}
              disabled={isLoading}
              className="min-h-12 rounded-lg border border-white/15 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/[0.06] disabled:opacity-40"
            >
              Review answer
            </button>
            <button
              type="button"
              onClick={onNextExercise}
              disabled={isLoading}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#FD954F] px-6 text-sm font-semibold text-[#17120E] transition-colors hover:bg-[#FFA968] disabled:cursor-wait disabled:opacity-65"
            >
              {isLoading && (
                <LoaderCircle
                  className="h-4 w-4 motion-safe:animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              )}
              {isLoading ? "Preparing next exercise" : "Next exercise"}
            </button>
          </div>
        </div>
      ) : (
        <>
          {feedback && (
            <div
              className={`mt-6 flex items-start gap-3 rounded-lg border p-4 ${
                isAlmostCorrect
                  ? "border-amber-400/35 bg-amber-400/[0.08]"
                  : "border-red-400/35 bg-red-400/[0.08]"
              }`}
              role="status"
              aria-live="polite"
            >
              <div className="relative mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/10">
                <Image
                  src="/ui/brand-kit/marks/chickytutor-face-mark-128.png"
                  alt=""
                  width={24}
                  height={24}
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <p className="font-semibold text-white">
                  {isAlmostCorrect ? "Almost there" : "Try again"}
                </p>
                <p className="mt-1 text-sm text-white/70">{feedback}</p>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => onCheckAnswer()}
            disabled={isLoading || (currentSentence.length === 0 && !typedAnswer.trim())}
            className="mt-6 min-h-14 w-full rounded-lg bg-[#FD954F] px-6 text-base font-semibold text-[#17120E] transition-colors hover:bg-[#FFA968] disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/35"
          >
            Check answer
          </button>
        </>
      )}

      {isLoading && (
        <p className="mt-3 text-sm text-white/55" role="status" aria-live="polite">
          Keeping this answer visible while the next exercise is prepared.
        </p>
      )}

      <ExplanationPanel
        showExplanation={showExplanation}
        onClose={() => setShowExplanation(false)}
        exerciseData={exerciseData}
        selectedLanguage={selectedLanguage}
        initialLanguageLevel={initialLanguageLevel}
      />
    </section>
  );
};

export default CanvasPracticePanel;
